/**
 * Kelime listelerini yükler.
 *
 * `valid.txt`  — geçerli tahminler; "kelime listesinde yok" uyarısı anında verilir.
 * `answers.txt`— cevap havuzu; çevrimdışı pratik modunun kelimeyi seçebilmesi için.
 *
 * Düello cevabı bu dosyalardan seçilmez: onu sunucu belirler ve maç bitene
 * kadar istemciye göndermez.
 */
import { chooseAnswer, RECENT_ANSWER_LIMIT, shuffledPoolBlock, type PoolClass } from "@mk/shared";
import { APP_EDITION } from "./edition.ts";

export interface WordLists {
  valid: ReadonlySet<string>;
  answers: readonly string[];
  extra: readonly string[];
}

let cache: Promise<WordLists> | null = null;

function parseList(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

async function fetchList(path: string): Promise<string[]> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Word list could not be loaded: ${path} (${res.status})`);
  return parseList(await res.text());
}

export function loadWordLists(): Promise<WordLists> {
  cache ??= (async () => {
    const [valid, answers, extra] = await Promise.all([
      fetchList("/valid.txt"), fetchList("/answers.txt"), fetchList("/answers-extra.txt"),
    ]);
    return { valid: new Set(valid), answers, extra };
  })();
  return cache;
}

/** Cevap havuzundan rastgele bir kelime — pratik modu için. */
export function pickRandomAnswer(answers: readonly string[], avoid?: string): string {
  if (answers.length === 0) throw new Error("Answer pool is empty");
  for (let i = 0; i < 8; i++) {
    const word = answers[Math.floor(Math.random() * answers.length)]!;
    if (word !== avoid) return word;
  }
  return answers[0]!;
}

interface PracticeSelection {
  remaining: PoolClass[];
  lastClass: PoolClass | null;
  recent: string[];
}

const fallback = new Map<string, PracticeSelection>();
const configuredExtraSlots = Number(import.meta.env.VITE_EXTRA_ANSWER_SLOTS ?? 2);
const practiceExtraSlots = Number.isInteger(configuredExtraSlots) && configuredExtraSlots >= 0 && configuredExtraSlots <= 4
  ? configuredExtraSlots : 2;

/** Device and account scoped, including an anonymous account for offline play. */
export function pickPracticeAnswer(lists: WordLists, userId: string | null, avoid?: string): string {
  const key = `mk-practice-selection:${APP_EDITION}:${userId ?? "anon"}`;
  let state = fallback.get(key);
  try {
    const saved = window.localStorage.getItem(key);
    if (saved) {
      const value = JSON.parse(saved) as PracticeSelection;
      if (Array.isArray(value.remaining) && Array.isArray(value.recent)) state = value;
    }
  } catch { /* Private browsing can disable storage. */ }
  state ??= { remaining: [], lastClass: null, recent: [] };
  const remaining = state.remaining.length ? [...state.remaining]
    : shuffledPoolBlock(state.lastClass, lists.extra.length ? practiceExtraSlots : 0);
  const requested = remaining.shift()!;
  const selected = chooseAnswer(
    { base: lists.answers, extra: lists.extra }, requested, state.recent,
    new Set(avoid ? [avoid] : []),
  );
  const next = {
    remaining,
    lastClass: requested,
    recent: [selected.answer, ...state.recent].slice(0, RECENT_ANSWER_LIMIT),
  };
  fallback.set(key, next);
  try { window.localStorage.setItem(key, JSON.stringify(next)); } catch { /* Continue in memory. */ }
  return selected.answer;
}
